<template>
    <re-b-modal ref="modal"
                :header="$t('label.sith.tpm.opcPointCurve.exportPdf')"
                :ok-label="$t('label.sith.tpm.opcPointCurve.pdfExport')"
                :ok-loading="exporting"
                large
                no-auto-hide
                disable-scrollable
                enable-popover
                no-close-on-backdrop
                @ok="exportPdf"
                @hide="onHide">
        <!-- 回车不导出：联想框里回车是选候选，普通输入框里回车也不该直接生成文件 -->
        <form class="opc-curve-pdf" @submit.prevent>
            <div class="form-group">
                <label for="opcCurvePdfTitle" class="mb-1">
                    {{ $t('label.sith.tpm.opcPointCurve.pdfTitle') }}<span class="text-danger ml-1">*</span>
                </label>
                <input id="opcCurvePdfTitle" type="text" class="form-control form-control-sm" autocomplete="off"
                       maxlength="100" v-model="title">
            </div>
            <div class="form-group">
                <label for="opcCurvePdfDevice" class="mb-1">{{ $t('label.sith.tpm.opcPointCurve.pdfDevice') }}</label>
                <input id="opcCurvePdfDevice" type="text" class="form-control form-control-sm" autocomplete="off"
                       maxlength="200" v-model="device">
            </div>
            <div class="form-row">
                <div class="form-group col-sm-6">
                    <label for="opcCurvePdfProject" class="mb-1">{{ $t('label.sith.tpm.opcPointCurve.projectCode') }}</label>
                    <single-suggest-input ref="project" v-model="projectCode"
                                          input-id="opcCurvePdfProject"
                                          :fetch="fetchProjects"
                                          :placeholder="$t('label.sith.tpm.opcPointCurve.projectCodePlaceholder')"
                                          :empty-text="$t('label.sith.tpm.opcPointCurve.noProjectMatch')"
                                          @select="onProjectSelect"></single-suggest-input>
                </div>
                <div class="form-group col-sm-6">
                    <label for="opcCurvePdfOrder" class="mb-1">{{ $t('label.sith.tpm.opcPointCurve.orderCode') }}</label>
                    <single-suggest-input ref="order" v-model="orderCode"
                                          input-id="opcCurvePdfOrder"
                                          :fetch="fetchOrders"
                                          :placeholder="$t('label.sith.tpm.opcPointCurve.orderCodePlaceholder')"
                                          :empty-text="orderEmptyText"
                                          @select="onOrderSelect">
                        <template #item="{item}">
                            <span>{{ item.value }}</span>
                            <span v-if="item.raw.projectCode" class="small text-muted ml-2">{{ item.raw.projectCode }}</span>
                        </template>
                    </single-suggest-input>
                </div>
            </div>
            <div class="form-row">
                <div class="form-group col-sm-6">
                    <label for="opcCurvePdfOperation" class="mb-1">
                        {{ $t('label.sith.tpm.opcPointCurve.operationSeq') }}
                        <i v-if="loadingOperations" class="fa fa-fw fa-spinner fa-spin text-muted"></i>
                    </label>
                    <single-suggest-input ref="operation" v-model="operationSeq"
                                          input-id="opcCurvePdfOperation"
                                          :min-length="0"
                                          :fetch="fetchOperationItems"
                                          :placeholder="$t('label.sith.tpm.opcPointCurve.operationSeqPlaceholder')"
                                          :empty-text="operationsEmptyText"></single-suggest-input>
                    <small v-if="operationsHint" class="form-text text-muted">{{ operationsHint }}</small>
                </div>
                <div class="form-group col-sm-6">
                    <label for="opcCurvePdfMaterial" class="mb-1">{{ $t('label.sith.tpm.opcPointCurve.materialCode') }}</label>
                    <input id="opcCurvePdfMaterial" type="text" class="form-control form-control-sm" autocomplete="off"
                           maxlength="100"
                           :placeholder="$t('label.sith.tpm.opcPointCurve.materialCodePlaceholder')"
                           v-model="materialCode">
                </div>
            </div>
            <div class="form-row">
                <div class="form-group col-sm-6">
                    <label for="opcCurvePdfOperator" class="mb-1">{{ $t('label.sith.tpm.opcPointCurve.operator') }}</label>
                    <input id="opcCurvePdfOperator" type="text" class="form-control form-control-sm" autocomplete="off"
                           maxlength="100" v-model="operator">
                </div>
            </div>
            <div class="small text-muted border-top pt-2">
                <i class="fa fa-fw fa-info-circle"></i>{{ hint }}
            </div>
        </form>
    </re-b-modal>
</template>

<script>
/* eslint-env browser */
import Vue from "vue";
import ReBModal from "@/components/echarts-part/CalendarShift/cells/stuffs/ReBModal.vue";
import SingleSuggestInput from "@/components/common/inputs/SingleSuggestInput.vue";
import OpcCurvePdfSheet from "@/components/SithMes/tpm/opcCurve/OpcCurvePdfSheet.vue";
import {
    fetchOperations,
    fetchOrderSuggest,
    fetchProjectSuggest
} from "@/components/SithMes/tpm/opcCurve/opcCurveQueryApi";
import {
    buildPdfFileName,
    createOffscreenHost,
    formatDateTime,
    formatRange,
    renderSheetToPdf,
    SHEET_CHART_HEIGHT_PX,
    SHEET_MARGIN_X_PX,
    SHEET_WIDTH_PX,
    textOrEmpty
} from "@/components/SithMes/tpm/opcCurve/opcCurvePdf";

const OPERATIONS_DEBOUNCE_MS = 300;

/**
 * OPC 点位曲线「导出 PDF」弹窗（验收 #9 / #10）。
 * 字段：标题（有默认值，可改）、设备（默认「编码 名称」，可改）、项目号 / 工单号 / 工序号（互相补全）、物料号（选填、不联想）、
 * 导出操作人（默认当前登录用户，可改）。
 * 互相补全：项目号前缀联想 → 选定后工单号联想限定在该项目下；选定工单 → 带出项目号、工序号列出该工单的工序；
 * 由炉子加热工单跳入（appliedQuery = 页面的 appliedRecord）时预填工单号 / 工序号 / 项目号 / 物料号；项目号为空时据工单号带出。
 *
 * 用法：this.$refs.pdfModal.open({deviceCode, deviceText, range, appliedQuery, getChartImage})
 *   getChartImage(size) => Promise<{dataUrl, width, height}>（页面传 OpcCurveChart.getViewImage）
 */
export default {
    name: "OpcCurvePdfModal",
    components: {ReBModal, SingleSuggestInput},
    data() {
        return {
            title: '',
            device: '',
            projectCode: '',
            orderCode: '',
            operationSeq: '',
            materialCode: '',
            operator: '',
            // 已知的「工单 → 项目」：选中工单候选或预填反查得到 {orderCode, projectCode}
            orderProject: null,
            // 当前已加载工序的工单号（null = 未加载）与其工序
            operationsOf: null,
            operations: [],
            loadingOperations: false,
            exporting: false,
            // 打开时页面传入：{deviceCode, range}
            context: null
        };
    },
    computed: {
        trimmedProjectCode() {
            return (this.projectCode || '').trim();
        },
        trimmedOrderCode() {
            return (this.orderCode || '').trim();
        },
        orderEmptyText() {
            return this.trimmedProjectCode
                ? this.$t('label.sith.tpm.opcPointCurve.noOrderMatchInProject')
                : this.$t('label.sith.tpm.opcPointCurve.noOrderMatch');
        },
        operationsEmptyText() {
            if (!this.trimmedOrderCode) {
                return this.$t('label.sith.tpm.opcPointCurve.orderFirst');
            }
            return this.operationsOf === this.trimmedOrderCode && !this.operations.length
                ? this.$t('label.sith.tpm.opcPointCurve.noOperations')
                : this.$t('label.sith.tpm.opcPointCurve.noOperationMatch');
        },
        operationsHint() {
            if (this.trimmedOrderCode && this.operationsOf === this.trimmedOrderCode && !this.operations.length
                && !this.loadingOperations) {
                return this.$t('label.sith.tpm.opcPointCurve.noOperations');
            }
            return '';
        },
        hint() {
            return this.$t('label.sith.tpm.opcPointCurve.pdfHint', {
                range: this.context ? formatRange(this.context.range) : ''
            });
        }
    },
    watch: {
        // 工单号变了（手输、选中或预填）：防抖后重新加载该工单的工序
        trimmedOrderCode(code) {
            this.clearOperationsTimer();
            if (code === this.operationsOf) {
                return;
            }
            this.operationsSeq++;
            this.operations = [];
            this.operationsOf = null;
            this.loadingOperations = false;
            if (code) {
                this.operationsTimer = setTimeout(() => this.loadOperations(code), OPERATIONS_DEBOUNCE_MS);
            }
        }
    },
    created() {
        // 非响应式：工序加载的防抖定时器与请求代次、预填反查项目号的代次、取图函数
        this.operationsTimer = null;
        this.operationsSeq = 0;
        this.prefillSeq = 0;
        this.getChartImage = null;
    },
    beforeDestroy() {
        this.clearOperationsTimer();
    },
    methods: {
        open({deviceCode, deviceText, range, appliedQuery, getChartImage}) {
            this.context = {deviceCode: deviceCode || '', range};
            this.getChartImage = getChartImage;
            this.exporting = false;
            this.title = this.$t('label.sith.tpm.opcPointCurve.pdfDefaultTitle');
            this.device = deviceText || deviceCode || '';
            this.projectCode = appliedQuery ? appliedQuery.projectCode || '' : '';
            this.materialCode = appliedQuery ? appliedQuery.materialCode || '' : '';
            this.operator = this.currentUserName();
            this.orderProject = null;
            this.orderCode = appliedQuery ? appliedQuery.orderCode || '' : '';
            this.operationSeq = appliedQuery ? appliedQuery.operationSeq || '' : '';
            this.$refs.modal.showModal();
            if (this.trimmedOrderCode) {
                this.prefillProject(this.trimmedOrderCode);
            }
        },
        // 当前登录用户显示名（App.vue 启动时 dispatch('userStore/getCurrentUser')，内容是后端 EmUser）
        currentUserName() {
            const holder = this.$store.state.userStore;
            const user = holder && holder.currentUser;
            return user ? (user.userName || user.loginName || '') : '';
        },
        // 预填的工单号 → 用工单联想反查它的项目号（用户已自己填了项目号或改了工单号则不覆盖）
        prefillProject(orderCode) {
            const seq = ++this.prefillSeq;
            fetchOrderSuggest(this.$store, orderCode).then(list => {
                if (seq !== this.prefillSeq || this.trimmedOrderCode !== orderCode) {
                    return;
                }
                const hit = list.find(o => o.orderCode === orderCode);
                if (!hit) {
                    return;
                }
                this.orderProject = {orderCode, projectCode: hit.projectCode || ''};
                if (!this.trimmedProjectCode && hit.projectCode) {
                    this.projectCode = hit.projectCode;
                }
            }).catch(() => {
                // 反查失败不影响导出，项目号留给用户手填
            });
        },
        // 关窗即作废在途请求并清空：下次 open 的预填值一定触发 watcher 重新加载工序
        onHide() {
            this.clearOperationsTimer();
            this.operationsSeq++;
            this.prefillSeq++;
            this.loadingOperations = false;
            this.operationsOf = null;
            this.operations = [];
            this.orderProject = null;
            this.orderCode = '';
            this.operationSeq = '';
        },
        clearOperationsTimer() {
            if (this.operationsTimer) {
                clearTimeout(this.operationsTimer);
                this.operationsTimer = null;
            }
        },
        toast(message, variant) {
            this.$bvToast.toast(message, {
                title: this.$t('label.sith.tpm.opcPointCurve.exportPdf'),
                variant: variant || 'warning',
                solid: true,
                autoHideDelay: 4000
            });
        },
        fetchProjects(keyword) {
            return fetchProjectSuggest(this.$store, keyword).then(list => list.map(code => ({
                value: code,
                label: code,
                raw: code
            })));
        },
        // 选定项目：已填的工单属于别的项目时清掉工单 / 工序，焦点交给工单号（其联想限定在该项目下）
        onProjectSelect(item) {
            this.prefillSeq++;
            const known = this.orderProject && this.orderProject.orderCode === this.trimmedOrderCode
                ? this.orderProject.projectCode : null;
            if (this.trimmedOrderCode && known !== null && known !== item.value) {
                this.orderCode = '';
                this.operationSeq = '';
                this.orderProject = null;
            }
            this.$nextTick(() => {
                if (this.$refs.order) {
                    this.$refs.order.focus();
                }
            });
        },
        fetchOrders(keyword) {
            return fetchOrderSuggest(this.$store, keyword, this.trimmedProjectCode).then(list => list.map(o => ({
                value: o.orderCode,
                label: o.projectCode ? o.orderCode + ' · ' + o.projectCode : o.orderCode,
                raw: o
            })));
        },
        // 选定工单：带出项目号，清空工序号并把焦点交给它（聚焦即列出该工单的工序）
        onOrderSelect(item) {
            this.prefillSeq++;
            const projectCode = (item.raw && item.raw.projectCode) || '';
            this.orderProject = {orderCode: item.value, projectCode};
            if (projectCode) {
                this.projectCode = projectCode;
            }
            this.operationSeq = '';
            this.$nextTick(() => {
                if (this.$refs.operation) {
                    this.$refs.operation.focus();
                }
            });
        },
        loadOperations(code) {
            this.clearOperationsTimer();
            const seq = ++this.operationsSeq;
            this.loadingOperations = true;
            return fetchOperations(this.$store, code).then(list => {
                if (seq !== this.operationsSeq) {
                    return;
                }
                this.operations = list;
                this.operationsOf = code;
            }).catch(() => {
                if (seq === this.operationsSeq) {
                    this.operations = [];
                    this.operationsOf = null;
                }
            }).finally(() => {
                if (seq === this.operationsSeq) {
                    this.loadingOperations = false;
                }
            });
        },
        // 工序号候选：该工单的工序按输入过滤（工序是本地列表，不再请求后端）
        fetchOperationItems(keyword) {
            const code = this.trimmedOrderCode;
            if (!code) {
                return Promise.resolve([]);
            }
            const ready = this.operationsOf === code ? Promise.resolve() : this.loadOperations(code);
            return ready.then(() => {
                const kw = (keyword || '').trim().toLowerCase();
                return this.operations
                    .filter(op => !kw || op.toLowerCase().indexOf(kw) !== -1)
                    .map(op => ({value: op, label: op, raw: op}));
            });
        },
        /**
         * 生成 PDF：取当前视图曲线图（按版面尺寸离屏重画）→ 离屏挂载 A4 版面 → html2canvas → jsPDF 下载。
         */
        exportPdf() {
            if (this.exporting || !this.context || typeof this.getChartImage !== 'function') {
                return;
            }
            const title = textOrEmpty(this.title);
            if (!title) {
                this.toast(this.$t('label.sith.tpm.opcPointCurve.pdfTitleRequired'));
                return;
            }
            const orderCode = this.trimmedOrderCode;
            const operationSeq = textOrEmpty(this.operationSeq);
            const fileName = buildPdfFileName({
                deviceCode: this.context.deviceCode,
                orderCode,
                operationSeq,
                middle: this.$t('label.sith.tpm.opcPointCurve.fileMiddle')
            });
            this.exporting = true;
            let host = null;
            let sheetVm = null;
            Promise.resolve().then(() => this.getChartImage({
                width: SHEET_WIDTH_PX - 2 * SHEET_MARGIN_X_PX,
                height: SHEET_CHART_HEIGHT_PX,
                pixelRatio: 2
            })).then(chartImage => {
                host = createOffscreenHost();
                const SheetCtor = Vue.extend(OpcCurvePdfSheet);
                sheetVm = new SheetCtor({
                    parent: this,
                    propsData: {
                        sheet: {
                            title,
                            device: textOrEmpty(this.device),
                            projectCode: this.trimmedProjectCode,
                            orderCode,
                            operationSeq,
                            materialCode: textOrEmpty(this.materialCode),
                            timeRange: formatRange(this.context.range),
                            operator: textOrEmpty(this.operator),
                            exportTime: formatDateTime(new Date(), true),
                            chartImage
                        }
                    }
                });
                sheetVm.$mount();
                host.appendChild(sheetVm.$el);
                return renderSheetToPdf(sheetVm.$el, fileName, title);
            }).then(() => {
                this.toast(this.$t('label.sith.tpm.opcPointCurve.pdfExported', {file: fileName}), 'success');
                this.$refs.modal.hide();
            }).catch(error => {
                console.error(error);
                this.toast(this.$t('label.sith.tpm.opcPointCurve.pdfFailed'), 'danger');
            }).finally(() => {
                this.exporting = false;
                if (sheetVm) {
                    sheetVm.$destroy();
                }
                if (host && host.parentNode) {
                    host.parentNode.removeChild(host);
                }
            });
        }
    }
};
</script>

<style scoped>
.opc-curve-pdf label {
    font-weight: normal;
}
</style>
